'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
  PickerRow,
  SettingsCard,
} from '@/shared/ui';
import { sortAlbumLocalesForUser } from '@/shared/constants';
import { useUser } from '@/app/model/stores';

interface EstimateLanguageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current output language (ISO 639-1, e.g. "en"). */
  value: string;
  /** Apply the new output language (takes effect on the next regenerate). */
  onSelect: (language: string) => void;
}

/**
 * Output-language drill-down for the estimate settings Language row
 * (album-language parity: same locale list and user-first sorting via
 * `sortAlbumLocalesForUser`). A generation input like currency/units —
 * selecting a row PATCHes the estimate; the generated texts pick the new
 * language up on the next regenerate.
 */
export function EstimateLanguageSheet({
  open,
  onOpenChange,
  value,
  onSelect,
}: EstimateLanguageSheetProps) {
  const t = useTranslations('album.settings');
  const user = useUser();

  // User's locale first, then English, then the rest alphabetically — the
  // exact album-language ordering.
  const sortedLanguages = useMemo(
    () => sortAlbumLocalesForUser(user?.locale),
    [user?.locale]
  );

  return (
    <NestedSheet open={open} onOpenChange={onOpenChange}>
      <NestedSheetContent>
        <NestedSheetHeader>
          <NestedSheetTitle>{t('language')}</NestedSheetTitle>
        </NestedSheetHeader>

        <NestedSheetBody className="px-4">
          <div className="py-2">
            <SettingsCard>
              {sortedLanguages.map((language) => (
                <PickerRow
                  key={language.code}
                  title={`${language.name} (${language.nativeName})`}
                  isSelected={language.code === value}
                  onClick={() => {
                    if (language.code !== value) onSelect(language.code);
                    onOpenChange(false);
                  }}
                />
              ))}
            </SettingsCard>
          </div>
        </NestedSheetBody>
      </NestedSheetContent>
    </NestedSheet>
  );
}
